import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import Card from 'eventtia-ui-components/lib/Card';
import { Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import Divider from '@material-ui/core/Divider';
import Avatar from '@material-ui/core/Avatar';
import InstagramIcon from '../../assets/InstagramIcon';
import callApi from '../../actions/callApi';
import CustomPropTypes from '../../helpers/CustomPropTypes';
import TimestampCard from '../../components/ActivityCard/TimestampCard';
import AttachmentFile from '../../components/AttachmentFile';
import BackButton from '../../components/BackButton';
import useStringTransforms from '../../hooks/useStringTransforms';
import { baseMoment } from '../../helpers/dates';
import { getWorkshopName } from '../../helpers/workshops';
import useEventUriHack from '../../hooks/hacks/useEventUriHack';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 194,
    margin: '-32px -52px 20px -52px',
    overflow: 'hidden',
  },
  tabletHeader: {
    marginLeft: -theme.spacing(6.5),
    marginRight: -theme.spacing(6.5),
  },
  mobileHeader: {
    marginLeft: -theme.spacing(4),
    marginRight: -theme.spacing(4),
  },
  backButton: {
    position: 'absolute',
    left: 52,
    top: 32,
    color: '#761D77',
  },
  bannerImage: {
    width: '100%',
    height: 194,
    objectFit: 'contain',
  },
  speakerDetails: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: theme.spacing(0.75),
  },
  speakerCard: {
    color: theme.palette.darkGrey.main,
    marginTop: theme.spacing(6.5),
    display: 'flex',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  avatar: {
    height: 72,
    width: 72,
    alignSelf: 'center',
    marginRight: theme.spacing(3),
    backgroundColor: theme.palette.lightGrey.main,
    color: theme.palette.primary.main,
  },
  contactInfo: {
    color: '#761D77',
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: '600',
    overflowWrap: 'anywhere',
    overflow: 'hidden',
    paddingBottom: 2,
    '&:hover': {
      '& $horizontalLine': {
        opacity: 1,
      },
    },
  },
  horizontalLine: {
    opacity: 0,
    width: '100%',
    height: 1.5,
    marginTop: -1,
    backgroundColor: '#761D77',
  },
  socialNetContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 1,
  },
  icon: {
    fontSize: '1.3rem',
    marginRight: theme.spacing(1),
  },
  name: {
    color: theme.palette.darkGrey.main,
    fontSize: 18,
    fontWeight: 'bold',
  },
  subtitle: {
    color: theme.palette.darkGrey.main,
    fontWeight: 700,
    fontSize: 16,
    margin: theme.spacing(5, 0, 2, 0),
  },
  biographyText: {
    color: theme.palette.darkGrey.light,
    lineHeight: 2,
    '& iframe': {
      maxWidth: '100%',
    },
  },
  verticalDivider: {
    margin: theme.spacing(0, 2),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  speakerData: {
    fontSize: '0.875rem',
    color: theme.palette.darkGrey.main,
  },
  socialMedia: {
    display: 'flex',
  },
  filesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  mobileFilesContainer: {
    flexDirection: 'column',
  },
  activities: {
    color: theme.palette.darkGrey.main,
    fontWeight: 600,
    fontSize: 16,
    margin: theme.spacing(5, 0, 4, 0),
  },
  activitiesWrapper: {
    paddingBottom: theme.spacing(9),
  },
}));

const urlRegexp = /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;

// const nickID = (link) => {
//   if (!urlRegexp.test(link)) return link;

//   if (link.endsWith('/')) {
//     const newID = link.slice(0, -1);
//     return newID.substr(newID.lastIndexOf('/') + 1);
//   }

//   return link.substr(link.lastIndexOf('/') + 1);
// };

const ContactInfo = ({ Icon, label, href }) => {
  const classes = useStyles();

  return (
    <Typography component="a" className={classes.contactInfo} href={href} rel="noopener noreferrer" target="_blank">
      <Icon className={classes.icon} />
      <div className={classes.socialNetContainer}>
        {label}
        <Divider className={classes.horizontalLine} orientation="horizontal" />
      </div>
    </Typography>
  );
};

ContactInfo.propTypes = {
  Icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
};

const Biography = ({
  speaker,
  callApi: dispatchCallApi,
  eventFiles,
  workshops,
  workshopDefinitions,
}) => {
  const classes = useStyles();
  const { eventUri, locale } = useParams();
  const { toSentenceCase } = useStringTransforms();
  const { t } = useTranslation(['speakers, global, schedule,']);
  const {
    id: speakerId,
    bio,
    fullName,
    position,
    company,
    instagram,
    linkedin,
    email,
    picture,
    workshops: workshopSpeaker,
    fileCategoryId,
  } = speaker;
  const dataWorkshops = (workshopSpeaker || []).filter(
    ({ id: workshopId }) => workshops[workshopId]
  )?.map(({ id: workshopId }) => {
    const workshop = workshops[workshopId];
    const {
      startDate, endDate,
    } = workshop;
    return {
      id: workshopId,
      name: getWorkshopName(workshop, workshopDefinitions, locale),
      startDate,
      endDate,
    };
  }).sort((a, b) => {
    if (baseMoment(a.startDate).isAfter(b.startDate, 'hour')) return 1;
    if (baseMoment(a.startDate).isBefore(b.startDate, 'hour')) return -1;
    return a.name.localeCompare(b.name, locale, { numeric: true });
  });

  // const at = position && company ? ` ${t('global:prepositions.at')} ` : '';
  const fullTitle = (position && company) ? `${toSentenceCase(position, true)} - ${toSentenceCase(company, true)}` : toSentenceCase(position || company || '', true);
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const tablet = useMediaQuery(theme.breakpoints.down('md'));
  useEffect(() => {
    if (speakerId && fileCategoryId) dispatchCallApi('speakersFiles', { eventUri, speakerId, fileCategoryId });
  }, [eventUri, speakerId, fileCategoryId, dispatchCallApi]);
  const speakerFiles = Object.values(eventFiles).filter((file) => file.entityType === 'Speaker' && file.entityId.toString() === speakerId);
  const hideBiography = useEventUriHack('hideBiographyText');

  return (
    <>
      <BackButton
        className={classes.backButton}
      />
      <Card className={classes.speakerCard}>
        <Avatar src={picture?.filename ? picture.small : null} className={classes.avatar} />
        {fullName && (
          <div className={classes.speakerDetails}>
            <Typography className={classes.name}>
              {fullName}
            </Typography>
            <Typography className={classes.speakerData}>
              {fullTitle}
            </Typography>
            <div className={classes.socialMedia}>
              {email && (
              <Typography className={classes.speakerData}>
                {email}
              </Typography>
              )}
              {linkedin && (
                <>
                  <Divider className={classes.verticalDivider} orientation="vertical" />
                  <ContactInfo
                    Icon={LinkedInIcon}
                    // label={mobile ? '' : nickID(linkedin)}
                    label={mobile ? '' : 'LinkedIn'}
                    href={urlRegexp.test(linkedin) ? linkedin : `https://www.linkedin.com/in/${linkedin}`}
                  />
                </>
              )}
              {instagram && (
                <>
                  <Divider className={classes.verticalDivider} orientation="vertical" />
                  <ContactInfo
                    Icon={InstagramIcon}
                    // label={mobile ? '' : nickID(instagram)}
                    label={mobile ? '' : 'Instagram'}
                    href={urlRegexp.test(instagram) ? instagram : `https://www.instagram.com/${instagram}`}
                  />
                </>
              )}
            </div>
          </div>
        )}
      </Card>
      {bio && (
      <div>
        {
          !hideBiography && (
          <Typography className={classes.subtitle}>
            {t('speakers:biography.title')}
          </Typography>
          )
        }

        <div
          className={classes.biographyText}
          dangerouslySetInnerHTML={{ __html: bio }}
        />
      </div>
      )}
      {!!speakerFiles.length && (
        <>
          <Typography className={classes.subtitle} variant="body1">
            {t('schedule:title.attachments')}
          </Typography>
          <div className={clsx(classes.filesContainer,
            mobile && classes.mobileFilesContainer)}
          >
            {speakerFiles.map((file) => (
              <AttachmentFile key={file.id} file={file} />
            ))}
          </div>
        </>
      )}
      {workshopSpeaker.length > 0 && (
        <>
          <Typography className={classes.activities}>
            {t('schedule:activityType.activities')}
          </Typography>
          <Grid className={classes.activitiesWrapper} spacing={2}>
            {dataWorkshops.map(({ id, name, startDate, endDate }, index) => (
              <>
                <Grid key={id}>
                  <TimestampCard
                    activity={workshops[id]}
                    id={id}
                    name={name}
                    startDate={startDate}
                    endDate={endDate}
                  />
                </Grid>
                {index < dataWorkshops.length - 1 && <Divider />}
              </>
            ))}
          </Grid>
        </>
      )}
    </>
  );
};

Biography.propTypes = {
  speaker: CustomPropTypes.speaker.isRequired,
  callApi: PropTypes.func.isRequired,
  eventFiles: PropTypes.objectOf(
    CustomPropTypes.eventFile
  ),
  workshops: PropTypes.objectOf(
    CustomPropTypes.workshop
  ),
  workshopDefinitions: PropTypes.objectOf(
    CustomPropTypes.workshopDefinition
  ),
};

Biography.defaultProps = {
  eventFiles: {},
  workshops: {},
  workshopDefinitions: {},
};

const mapStateToProps = ({
  entities: {
    eventFiles,
    workshops,
    workshopDefinitions,
  },
}) => ({
  eventFiles,
  workshops,
  workshopDefinitions,
});

export default connect(mapStateToProps, { callApi })(Biography);
